/**
 * Resolucao de distratores a partir de solucionadores defeituosos.
 *
 * A POLITICA QUE PRECISA SER RECUSADA: `nudge`. Empurrar um distrator colidente por
 * um epsilon para torna-lo distinto destroi a premissa inteira -- aquela alternativa
 * deixaria de ser a saida de um erro nomeado, `evidence.matchedError` passaria a
 * mentir, e a analitica de distratores ficaria construida sobre ficcao. Jogar seeds
 * fora e barato; corromper a ancoragem em erro nao e.
 *
 * ESCREVA 6-7 SOLUCIONADORES POR TEMPLATE, NAO 4. Com exatamente 4, qualquer
 * NOT_APPLICABLE ou colisao mata a seed e a taxa de rejeicao vai a 40-60%. Com 7
 * ranqueados, ela cai abaixo de 5% e os erros mais valiosos dominam o banco.
 */

import { hashOf } from '../core/hash.ts';
import { relativeSeparation } from '../core/num.ts';
import type { BuggySolver, Maybe, ResolvedDistractor, SolveCtx } from '../schema/item.ts';
import { NOT_APPLICABLE } from '../schema/item.ts';
import type { ErrorCode } from '../schema/errors.ts';

export type RejectionReason =
  'sampling-failure' | 'distractor-collision' | 'invariant-violation' | 'build-error';

export interface DistractorOptions<A> {
  readonly need: number;
  readonly eq?: (x: A, y: A) => boolean;
  /** Separacao relativa minima entre alternativas numericas. */
  readonly minRelSep?: number;
}

export interface DistractorDiagnostics {
  readonly notApplicable: readonly ErrorCode[];
  readonly collidedWithKey: readonly ErrorCode[];
  readonly collidedWithOther: readonly ErrorCode[];
  readonly tooClose: readonly ErrorCode[];
}

export type DistractorResult<A> =
  | {
      readonly status: 'ok';
      readonly distractors: readonly ResolvedDistractor<A>[];
      readonly diagnostics: DistractorDiagnostics;
    }
  | {
      readonly status: 'rejected';
      readonly reason: RejectionReason;
      readonly detail: string;
      readonly diagnostics: DistractorDiagnostics;
    };

/** Agrupa por prioridade e embaralha dentro de cada faixa, de forma reprodutível. */
function orderByBands<P, A>(
  buggy: readonly BuggySolver<P, A>[],
  ctx: SolveCtx,
): BuggySolver<P, A>[] {
  const faixas = new Map<number, BuggySolver<P, A>[]>();
  for (const b of buggy) {
    const lista = faixas.get(b.priority);
    if (lista) lista.push(b);
    else faixas.set(b.priority, [b]);
  }

  const out: BuggySolver<P, A>[] = [];
  for (const p of [...faixas.keys()].sort((a, b) => a - b)) {
    const lista = faixas.get(p) as BuggySolver<P, A>[];
    // Sub-fluxo próprio por faixa: acrescentar um solucionador numa faixa não
    // desloca a ordem das outras.
    out.push(...(lista.length > 1 ? ctx.rng.fork(`faixa:${p}`).shuffle(lista) : lista));
  }
  return out;
}

const defaultEq = <A>(x: A, y: A): boolean =>
  typeof x === 'number' && typeof y === 'number' ? Math.abs(x - y) < 1e-9 : hashOf(x) === hashOf(y);

export function resolveDistractors<P, A>(
  params: P,
  key: A,
  buggy: readonly BuggySolver<P, A>[],
  ctx: SolveCtx,
  opts: DistractorOptions<A>,
): DistractorResult<A> {
  const eq = opts.eq ?? defaultEq;
  const minRelSep = opts.minRelSep ?? 0.02;

  const notApplicable: ErrorCode[] = [];
  const collidedWithKey: ErrorCode[] = [];
  const collidedWithOther: ErrorCode[] = [];
  const tooClose: ErrorCode[] = [];

  const accepted: ResolvedDistractor<A>[] = [];

  // 1. ordena por FAIXAS de prioridade (menor = mais valioso pedagogicamente),
  //    embaralhando DENTRO de cada faixa.
  //
  //    Isto não é enfeite: com uma ordem totalmente determinística, o mesmo conjunto
  //    de distratores é escolhido em toda seed, e como as alternativas numéricas vão
  //    em ordem crescente, a chave cai SEMPRE na mesma posição. Um aluno treinado
  //    aprende a marcar aquela letra. Embaralhar dentro da faixa faz variar quantos
  //    distratores ficam abaixo da chave — e com isso a posição dela.
  //
  //    Coloque na faixa 1 o erro que dá nome ao task-model (esse deve aparecer
  //    sempre) e agrupe os demais em faixas de valor pedagógico equivalente.
  const ordered = orderByBands(buggy, ctx);

  for (const solver of ordered) {
    if (accepted.length >= opts.need) break;

    // Cada solucionador recebe um sub-fluxo proprio: eles nao interferem entre si,
    // e acrescentar um solver novo nao desloca os numeros dos outros.
    const subCtx: SolveCtx = { ...ctx, rng: ctx.rng.fork(`buggy:${solver.error}`) };

    // Uma exceção aqui NÃO é "o erro não se aplica": para isso existe NOT_APPLICABLE.
    // É defeito do solucionador, e sobe até `generate`, que recusa a seed como
    // 'build-error' — o que a bateria não tolera. Engolida como não-aplicável, ela
    // apenas trocava um distrator por outro, sem ninguém saber.
    const value: Maybe<A> = solver.solve(params, subCtx);

    // 2. o erro nao pode ocorrer com estes parametros
    if (value === NOT_APPLICABLE) {
      notApplicable.push(solver.error);
      continue;
    }
    const v = value as A;

    if (typeof v === 'number' && !Number.isFinite(v)) {
      notApplicable.push(solver.error);
      continue;
    }

    // 3. colidiu com a chave -> NAO e distrator: e prova de que os parametros
    //    escolhidos nao discriminam este erro. Descartar e registrar.
    if (eq(v, key)) {
      collidedWithKey.push(solver.error);
      continue;
    }

    // 4. colidiu com um distrator ja aceito (o de maior prioridade fica)
    if (accepted.some((d) => eq(d.value, v))) {
      collidedWithOther.push(solver.error);
      continue;
    }

    // 5. proximo demais de alguma alternativa ja presente
    //    (regra do catalogo: "nunca dois distratores praticamente identicos")
    if (typeof v === 'number') {
      const outros = [key, ...accepted.map((d) => d.value)].filter(
        (x): x is A & number => typeof x === 'number',
      );
      if (outros.some((o) => relativeSeparation(o, v) < minRelSep)) {
        tooClose.push(solver.error);
        continue;
      }
    }

    // A explicação é calculada AQUI, com o valor aceito, para nunca divergir da
    // alternativa que o aluno vai ver. Uma explicação que lança é defeito como o do
    // solucionador, e segue o mesmo caminho: engolida, o distrator ia para a tela sem
    // comentário, e só a auditoria — com doze seeds por item — tinha chance de ver.
    const explanation: ResolvedDistractor<A>['explanation'] = solver.explain?.(params, v);
    accepted.push(
      explanation
        ? { error: solver.error, value: v, explanation }
        : { error: solver.error, value: v },
    );
  }

  const diagnostics: DistractorDiagnostics = {
    notApplicable,
    collidedWithKey,
    collidedWithOther,
    tooClose,
  };

  // 6. sobraram menos que o necessario -> rejeita a seed. NUNCA fabricar enchimento.
  if (accepted.length < opts.need) {
    return {
      status: 'rejected',
      reason: 'distractor-collision',
      detail:
        `apenas ${accepted.length} de ${opts.need} distratores sobreviveram ` +
        `(nao-aplicaveis: ${notApplicable.length}, colidiram com a chave: ${collidedWithKey.length}, ` +
        `colidiram entre si: ${collidedWithOther.length}, proximos demais: ${tooClose.length})`,
      diagnostics,
    };
  }

  return { status: 'ok', distractors: accepted.slice(0, opts.need), diagnostics };
}
